import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Generation } from '../api';
import { formatAgo, formatCredits, formatDate } from '../format';
import { CopyButton, Empty, PageHead, Pill, TableWrap } from '../ui';

const statuses = ['', 'DONE', 'PROCESSING', 'FAILED', 'REJECTED'];

// The same history the API answers at GET /v1/generations. It is here so a
// developer can see what their integration did without writing a script to ask.
export default function Generations() {
  const [items, setItems] = useState<Generation[]>([]);
  const [status, setStatus] = useState('');
  const [nextBefore, setNextBefore] = useState<number | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(
    async (before?: number) => {
      setBusy(true);
      setError('');
      try {
        const page = await api.generations(before, status || undefined);
        setItems((current) => (before ? [...current, ...page.items] : page.items));
        setNextBefore(page.next_before);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load generations');
      } finally {
        setBusy(false);
      }
    },
    [status],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHead
        title="Generations"
        lead="Everything your keys have started, newest first — the same list GET /v1/generations returns."
      />

      <div className="panel">
        <div className="panel-head">
          <div className="row">
            <select
              aria-label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              {statuses.map((value) => (
                <option key={value || 'all'} value={value}>
                  {value ? value.toLowerCase() : 'Every status'}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <span className="muted small">
              {items.length} shown{nextBefore ? '+' : ''}
            </span>
            <button className="secondary" onClick={() => load()} disabled={busy}>
              {busy ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {error ? <p className="error">{error}</p> : null}

        {items.length === 0 && !busy ? (
          <Empty title="Nothing here yet.">
            A generation appears the moment a key posts one. The call is on the{' '}
            <Link to="/docs">Docs</Link> page.
          </Empty>
        ) : (
          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th className="optional">Template</th>
                  <th className="num">Credits</th>
                  <th>Started</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {items.map((generation) => (
                  <tr key={generation.id}>
                    <td className="mono small">{generation.id}</td>
                    <td>
                      <Pill status={generation.status} />
                      {generation.error ? (
                        <div className="muted small" style={{ marginTop: 4 }}>
                          {generation.error}
                        </div>
                      ) : null}
                    </td>
                    <td className="mono small optional">{generation.template_id ?? ''}</td>
                    <td className="num">{formatCredits(generation.price_credits)}</td>
                    {/* Relative first, because the question is "did my last call
                        land"; the exact time is on hover for the one case where
                        it matters. */}
                    <td className="small" title={formatDate(generation.created_at)}>
                      {formatAgo(generation.created_at)}
                    </td>
                    <td className="small">
                      {generation.result_url ? (
                        <span className="row" style={{ gap: 8, alignItems: 'center' }}>
                          <a href={generation.result_url} target="_blank" rel="noreferrer">
                            open
                          </a>
                          <CopyButton
                            text={generation.result_url}
                            label="link"
                            className="secondary small"
                          />
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}

        {nextBefore ? (
          <p>
            <button className="secondary" onClick={() => load(nextBefore)} disabled={busy}>
              Load more
            </button>
          </p>
        ) : null}

        <p className="panel-note">
          A rejected generation was refused by the content policy and its credits were returned;
          failed is ours and is refunded the same way. Result links expire — fetch what you keep.
        </p>
      </div>
    </>
  );
}
